import React, { useState, useEffect, useCallback } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Box, Button, Grid, Typography } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useSnackbar } from '../../context/SnackbarContext';
import LoadingComponent from '../../components/loading';
import { getCampaigns, archiveCampaign, Campaign } from '../../api/campaigns';
import WebTopbar from '../../components/topbar/WebTopbar';
import CampaignCard from './components/CampaignCard';
import EmptyState from './components/EmptyState';

const INITIAL_OFFSET = 0;
const INITIAL_LIMIT = 50;

const CampaignArchivedPage: React.FC = () => {
  const { showSnackbar } = useSnackbar();
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    getCampaigns(INITIAL_OFFSET, INITIAL_LIMIT, true)
      .then((response) => {
        setCampaigns(response.campaigns || []);
      })
      .catch((error) => {
        const errorMessage =
          error instanceof Error
            ? error.message
            : 'Failed to load archived campaigns';
        showSnackbar(errorMessage, 'error');
        setCampaigns([]);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [showSnackbar]);

  const handleUnarchive = useCallback(
    async (campaign: Campaign, archived: boolean) => {
      if (archived) return;
      try {
        await archiveCampaign(campaign.id, false);
        setCampaigns((prev) => prev.filter((c) => c.id !== campaign.id));
        showSnackbar('Campaign unarchived', 'success');
      } catch (error) {
        const errorMessage =
          error instanceof Error
            ? error.message
            : 'Failed to unarchive campaign';
        showSnackbar(errorMessage, 'error');
      }
    },
    [showSnackbar]
  );

  if (loading) {
    return <LoadingComponent message="Loading archived campaigns..." />;
  }

  return (
    <>
      <WebTopbar />

      <Box
        sx={{
          width: '100%',
          height: '100%',
          padding: '24px 48px',
          paddingBottom: '8px',
          overflow: 'auto',
          boxSizing: 'border-box',
          backgroundColor: (theme) => theme.customColors.themeSecondary
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '32px'
          }}
        >
          <Box>
            <Button
              component={RouterLink}
              to="/campaign"
              startIcon={<ArrowBackIcon />}
              sx={{ textTransform: 'none', marginBottom: 1 }}
            >
              Back to campaigns
            </Button>
            <Typography
              variant="h4"
              sx={{
                fontWeight: '700',
                color: (theme) => theme.customColors.themeNeutralPrimaryColor,
                marginBottom: '8px'
              }}
            >
              Archived campaigns
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: (theme) => theme.palette.text.secondary
              }}
            >
              {campaigns.length === 0
                ? 'No archived campaigns'
                : `${campaigns.length} archived ${campaigns.length === 1 ? 'campaign' : 'campaigns'}`}
            </Typography>
          </Box>
        </Box>

        {campaigns.length === 0 ? (
          <EmptyState title="No archived campaigns" showIcon={false} />
        ) : (
          <Grid container spacing={3}>
            {campaigns.map((campaign) => (
              <Grid item xs={12} sm={6} md={4} key={campaign.id}>
                <CampaignCard
                  campaign={campaign}
                  isArchivedView
                  onArchive={handleUnarchive}
                />
              </Grid>
            ))}
          </Grid>
        )}
      </Box>
    </>
  );
};

export default CampaignArchivedPage;
